// src/pages/warehouse/JournalPage.tsx
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useJournal, useJournalFacets } from '@/api/warehouse'
import { useAuth } from '@/api/auth'
import { Input } from '@/components/ui/input'
import { WarehouseCompanyFilter } from '@/components/warehouse/WarehouseCompanyFilter'
import { warehouseErrorMessage, type WarehouseCompanyFilter as CompanyFilter } from '@/lib/warehouse-company'
import { Button } from '@/components/ui/button'

const PAGE_SIZE = 200

/** Modul 22 A: Platz-Anzeige aus den in listJournal aufgeloesten Barcodes
 *  (R##+F##+P#+EB#). Bewegungen mit Quelle UND Ziel (z.B. Umfahren) zeigen beide,
 *  reine Entnahmen (pick_rest/-entnahme) nur den Quell-Platz. */
function formatPlatz(m: Record<string, unknown>): string {
  const from = m['placeFrom'] ? String(m['placeFrom']) : null
  const to = m['placeTo'] ? String(m['placeTo']) : null
  if (from && to) return `${from} → ${to}`
  return from ?? to ?? '—'
}

const NEUTRAL_LABEL = 'Gesamtes Journal – Firma nicht ausgewiesen'
type JournalViewMode = CompanyFilter | 'neutral'
type Filter = { artikel: string; place: string; action: string; actor_name: string; device: string; from: string; to: string }
const LEER: Filter = { artikel: '', place: '', action: '', actor_name: '', device: '', from: '', to: '' }

/** Freitext-Felder erst nach einer kurzen Tipp-Pause abfragen — sonst geht je Tastendruck
 *  ein Request raus und die Seite springt bei jedem Zeichen auf Seite 1 zurueck. */
function useDebounced<T>(value: T, ms = 400): T {
  const [v, setV] = useState(value)
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}

/** <input type="date"> meldet beim Tippen Zwischenstaende wie „0002-10-12" — nur ein
 *  vollstaendiges Datum ab 2000 geht an den Server, sonst kommt kurz das ganze Journal. */
function fertigesDatum(v: string) {
  return /^20\d\d-\d\d-\d\d$/.test(v) ? v : ''
}

/** Manche Buchungen legen Roh-JSON in die Notiz (z.B. place / pallet_ersatz_auslagerung, ~650
 *  Zeichen ohne Leerzeichen) — das sprengte die Tabellenbreite. Aufklappbar statt Fliesstext. */
function Notiz({ note }: { note: unknown }) {
  const text = note == null ? '' : String(note)
  if (!/^\s*[{[]/.test(text)) return <>{text}</>
  let schoen = text
  try { schoen = JSON.stringify(JSON.parse(text), null, 2) } catch { /* kein gueltiges JSON: roh zeigen */ }
  return (
    <details>
      <summary className="cursor-pointer text-xs text-muted">Technische Details</summary>
      <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-all text-xs">{schoen}</pre>
    </details>
  )
}

function JournalGroup({ filter, company, label, showTitle }: { filter: Record<string, string>; company: CompanyFilter; label: string; showTitle: boolean }) {
  const [offset, setOffset] = useState(0)
  const top = useRef<HTMLElement>(null)
  const journal = useJournal(offset ? { ...filter, offset: String(offset) } : filter, company)
  const rows = journal.isError ? [] : journal.data ?? []
  const laedt = journal.isFetching || journal.isPlaceholderData
  const blaettern = (next: number) => { setOffset(next); top.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }) }
  return (
    <section ref={top} aria-label={label} className="space-y-2 scroll-mt-4">
      {showTitle && <h2 className="text-lg font-semibold">{label}</h2>}
      {journal.isError && <div role="alert" className="text-sm text-red-600">{warehouseErrorMessage(journal.error)} <Button variant="outline" onClick={() => void journal.refetch()}>Ansicht neu laden</Button></div>}
      <div className={`overflow-x-auto rounded-xl border border-line ${laedt ? 'opacity-60' : ''}`}>
        <table className="w-full min-w-[720px] table-fixed text-sm">
          <colgroup>
            <col className="w-[92px]" /><col className="w-[165px]" /><col /><col className="w-[150px]" /><col /><col className="w-[130px]" />
          </colgroup>
          <thead className="bg-panel text-left"><tr><th className="p-2">Zeit</th><th className="p-2">Aktion</th><th className="p-2">Artikel</th><th className="p-2">Platz</th><th className="p-2">Details</th><th className="p-2">Wer / Gerät</th></tr></thead>
          <tbody>
            {rows.map((m) => {
              const zeit = new Date(String(m['createdAt']))
              return (
                <tr key={String(m['id'])} className="border-t border-line align-top">
                  <td className="p-2 tabular-nums">{zeit.toLocaleDateString('de-DE')}<div className="text-xs text-muted">{zeit.toLocaleTimeString('de-DE')}</div></td>
                  <td className="p-2 font-mono text-xs break-words">{String(m['action'])}</td>
                  {/* Sternnummer/Artikel kommen aus dem Join in listJournal — nicht jede
                      Aktion hat eine Position (z.B. receipt_created), dann bleibt '—'. */}
                  <td className="p-2 break-words">
                    <GrundartikelLink className="font-mono" code={m['merchantSku'] ? String(m['merchantSku']) : null} />
                    {m['itemName'] ? <div className="text-xs text-muted">{String(m['itemName'])}</div> : null}
                  </td>
                  <td className="p-2 font-mono text-xs break-words">{formatPlatz(m)}</td>
                  <td className="p-2 break-words"><Notiz note={m['note']} /></td>
                  <td className="p-2 break-words">{String(m['actorName'] ?? '—')}<div className="text-xs text-muted">{String(m['deviceLabel'] ?? '—')}</div></td>
                </tr>
              )
            })}
            {rows.length === 0 && !journal.isLoading && !journal.isError && <tr><td colSpan={6} className="p-4 text-center text-muted">Keine Bewegungen im Filter.</td></tr>}
          </tbody>
        </table>
      </div>
      {!journal.isError && <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <span>{rows.length ? `Einträge ${offset + 1}–${offset + rows.length}` : '0 Einträge'} · Seite {offset / PAGE_SIZE + 1} · höchstens {PAGE_SIZE} je Seite</span>
        <Button size="sm" variant="outline" disabled={offset === 0 || laedt} onClick={() => blaettern(Math.max(0, offset - PAGE_SIZE))}>Vorherige Seite</Button>
        <Button size="sm" variant="outline" disabled={rows.length < PAGE_SIZE || laedt} onClick={() => blaettern(offset + PAGE_SIZE)}>Nächste Seite</Button>
      </div>}
    </section>
  )
}

function Auswahl({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (v: string) => void }) {
  return (
    <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}
      className="h-9 w-44 rounded-md border border-line bg-panel px-2 text-sm focus-visible:outline-2 focus-visible:outline-primary">
      <option value="">{label}: alle</option>
      {/* Gewaehlter Wert bleibt waehlbar, auch wenn er (noch) nicht in den Auswahlwerten steht. */}
      {[...new Set(value ? [value, ...options] : options)].map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  )
}

/** Journal ohne Seitenrahmen — steht als Reiter „Journal" in den Leiter-Listen und
 *  als eigene Seite fuer Lager-Nutzer ohne Leiter-Recht. */
export function JournalView({ defaultCompany = 'both' }: { defaultCompany?: JournalViewMode } = {}) {
  const { warehouse } = useAuth()
  const facets = useJournalFacets()
  const [company, setCompany] = useState<JournalViewMode>(defaultCompany)
  const [f, setF] = useState<Filter>(LEER)
  const artikel = useDebounced(f.artikel)
  const place = useDebounced(f.place)
  const from = useDebounced(fertigesDatum(f.from))
  const to = useDebounced(fertigesDatum(f.to))
  const filter: Record<string, string> = { limit: String(PAGE_SIZE) }
  for (const [k, v] of Object.entries({ ...f, artikel, place, from, to })) if (v.trim()) filter[k] = v.trim()
  const groups = !warehouse.lead ? [{ company: 'both' as const, label: 'Stahlia' }]
    : company === 'neutral' ? [{ company: 'both' as const, label: NEUTRAL_LABEL }]
    : (company === 'both' ? ['stahlia', 'priostahl'] as const : [company]).map(scope => ({ company: scope, label: scope === 'priostahl' ? 'Priostahl' : 'Stahlia' }))
  const gefiltert = Object.values(f).some(Boolean)
  return (
    <div className="space-y-3">
      <WarehouseCompanyFilter value={company} onChange={setCompany} includeNeutral />
      {warehouse.lead && <p className="text-xs text-muted">
        {company === 'neutral'
          ? 'Diese Gesamtansicht enthält alle zugänglichen Bewegungen, auch bereits zugeordnete und firmenübergreifende Ereignisse. Für einzelne Bewegungen wird hier keine Firma ausgewiesen.'
          : 'Die Listen zeigen Bewegungen je Firma. Firmenübergreifende oder nicht zugeordnete Ereignisse bleiben über „Gesamtes Journal – Firma nicht ausgewiesen“ erreichbar.'}
      </p>}
      <div className="flex flex-wrap items-center gap-2">
        <Input className="w-36" placeholder="Artikel (Sternnr.)" value={f.artikel} onChange={(e) => setF({ ...f, artikel: e.target.value })} />
        <Input className="w-44" placeholder="Platz-Barcode" value={f.place} onChange={(e) => setF({ ...f, place: e.target.value })} />
        <Auswahl label="Aktion" value={f.action} options={facets.data?.actions ?? []} onChange={(v) => setF({ ...f, action: v })} />
        <Auswahl label="Wer" value={f.actor_name} options={facets.data?.actors ?? []} onChange={(v) => setF({ ...f, actor_name: v })} />
        <Auswahl label="Gerät" value={f.device} options={facets.data?.devices ?? []} onChange={(v) => setF({ ...f, device: v })} />
        <label className="flex items-center gap-1 text-sm">von<Input aria-label="von" className="w-40" type="date" value={f.from} max={f.to || undefined} onChange={(e) => setF({ ...f, from: e.target.value })} /></label>
        <label className="flex items-center gap-1 text-sm">bis<Input aria-label="bis" className="w-40" type="date" value={f.to} min={f.from || undefined} onChange={(e) => setF({ ...f, to: e.target.value })} /></label>
        {gefiltert && <Button size="sm" variant="ghost" onClick={() => setF(LEER)}>Filter zurücksetzen</Button>}
      </div>
      {groups.map(group => <JournalGroup key={`${group.label}:${JSON.stringify(filter)}`} filter={filter} company={group.company} label={group.label} showTitle={warehouse.lead} />)}
    </div>
  )
}

export default function JournalPage() {
  return (
    <div className="p-4 space-y-3 max-w-6xl mx-auto">
      <h1 className="text-xl font-bold">Bewegungs-Journal</h1>
      <JournalView />
    </div>
  )
}

/** /warehouse/journal: Leiter landen im Reiter „Journal" der Leiter-Listen (User 2026-10-15);
 *  Lager-Nutzer ohne Leiter-Recht behalten die eigene Seite. */
export function JournalRoute() {
  const { warehouse } = useAuth()
  return warehouse.lead ? <Navigate to="/warehouse/leiter?tab=journal" replace /> : <JournalPage />
}
